import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { usePinFilters } from "@/lib/pins-context";
import * as api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ChevronDown, X, Search } from "lucide-react";

interface SchoolOption {
  id: string;
  name: string;
}

/**
 * Unified school filter for all school-scoped list pages.
 *
 * - School admin / coach (few schools): pre-loaded checkbox list
 * - Org admin (moderate): pre-loaded with search
 * - Super admin (25k+): server-side search, no pre-load
 *
 * Selected school IDs are stored in URL search params under `schools=id1,id2`.
 */
export function SchoolFilter() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const roles = user?.roles ?? [];
  const isSuperAdmin = roles.some((r) => r.role_type === "super_admin");
  const isOrgAdmin = roles.some((r) => r.role_type === "org_admin");
  const needsAsyncSearch = isSuperAdmin;

  // --- Selected IDs from URL ---
  const selectedIds = useMemo(() => {
    const param = searchParams.get("schools");
    return param ? new Set(param.split(",")) : new Set<string>();
  }, [searchParams]);

  function updateSelected(next: Set<string>) {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (next.size > 0) p.set("schools", [...next].join(","));
      else p.delete("schools");
      p.delete("page"); // reset pagination on filter change
      return p;
    }, { replace: true });
  }

  function toggle(id: string) {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    updateSelected(next);
  }

  function clear() {
    updateSelected(new Set());
    setOpen(false);
  }

  // --- Pre-loaded schools (school admin, org admin) ---
  const [preloaded, setPreloaded] = useState<SchoolOption[]>([]);
  const [preloadDone, setPreloadDone] = useState(false);

  useEffect(() => {
    if (needsAsyncSearch) {
      setPreloadDone(true);
      return;
    }
    if (isOrgAdmin) {
      api.getSchools()
        .then((res) => setPreloaded(res.schools.map((s) => ({ id: s.id, name: s.display_name ?? s.name }))))
        .catch(() => {})
        .finally(() => setPreloadDone(true));
    } else {
      // School admin / coach -- derive from roles
      const schoolIds = [...new Set(roles.filter((r) => r.scope_type === "school").map((r) => r.scope_id))];
      Promise.all(schoolIds.map((id) => api.getSchool(id).then((r) => ({ id: r.school.id, name: r.school.display_name ?? r.school.name })).catch(() => null)))
        .then((results) => setPreloaded(results.filter(Boolean) as SchoolOption[]))
        .finally(() => setPreloadDone(true));
    }
  }, [needsAsyncSearch, isOrgAdmin, roles.length]);

  // --- Async search results (super admin) ---
  const [searchResults, setSearchResults] = useState<SchoolOption[]>([]);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const doSearch = useCallback((q: string) => {
    if (!needsAsyncSearch) return;
    if (!q.trim()) { setSearchResults([]); return; }
    setSearching(true);
    api.searchSchools(q.trim(), { limit: 20 })
      .then((res) => setSearchResults(res.schools.map((s) => ({ id: s.id, name: s.display_name ?? s.name }))))
      .catch(() => setSearchResults([]))
      .finally(() => setSearching(false));
  }, [needsAsyncSearch]);

  function handleSearchChange(q: string) {
    setSearch(q);
    if (needsAsyncSearch) {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => doSearch(q), 300);
    }
  }

  // --- Resolve names for selected schools (needed for super admin where schools aren't pre-loaded) ---
  const [selectedSchools, setSelectedSchools] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    if (!needsAsyncSearch) return;
    // Fetch names for selected IDs we don't already know
    const unknown = [...selectedIds].filter((id) => !selectedSchools.has(id));
    if (unknown.length === 0) return;
    Promise.all(unknown.map((id) =>
      api.getSchool(id).then((r) => [id, r.school.display_name ?? r.school.name] as const).catch(() => null),
    )).then((results) => {
      setSelectedSchools((prev) => {
        const next = new Map(prev);
        for (const r of results) { if (r) next.set(r[0], r[1]); }
        return next;
      });
    });
  }, [[...selectedIds].join(","), needsAsyncSearch]);

  // --- Dropdown open/close ---
  useEffect(() => {
    if (!open) { setSearch(""); setSearchResults([]); return; }
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    setTimeout(() => searchRef.current?.focus(), 0);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  // --- Build display options ---
  const options: SchoolOption[] = useMemo(() => {
    if (needsAsyncSearch) {
      // Merge selected (resolved) + search results, deduped
      const map = new Map<string, string>();
      // Selected first
      for (const id of selectedIds) {
        const name = selectedSchools.get(id);
        if (name) map.set(id, name);
      }
      // Then search results
      for (const s of searchResults) {
        if (!map.has(s.id)) map.set(s.id, s.name);
      }
      return [...map.entries()].map(([id, name]) => ({ id, name }));
    }
    // Pre-loaded: filter by search
    const q = search.toLowerCase().trim();
    return q ? preloaded.filter((s) => s.name.toLowerCase().includes(q)) : preloaded;
  }, [needsAsyncSearch, preloaded, searchResults, selectedIds, selectedSchools, search]);

  // --- Don't render if only 1 school available (no choice to make) ---
  if (preloadDone && !needsAsyncSearch && preloaded.length <= 1) return null;
  if (!preloadDone) return null;

  const showSearch = needsAsyncSearch || preloaded.length > 10;

  return (
    <div className="relative" ref={ref}>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="gap-1.5"
        onClick={() => setOpen((o) => !o)}
      >
        Schools
        {selectedIds.size > 0 && (
          <span className="ml-0.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-xs text-primary-foreground">
            {selectedIds.size}
          </span>
        )}
        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
      </Button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 min-w-[280px] rounded-md border border-border bg-background shadow-md flex flex-col">
          {showSearch && (
            <div className="p-1.5 border-b border-border flex items-center gap-1.5">
              <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <input
                ref={searchRef}
                type="text"
                placeholder={needsAsyncSearch ? "Search schools..." : "Filter..."}
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              />
            </div>
          )}
          <div className="overflow-y-auto p-1 max-h-[260px]">
            {selectedIds.size > 0 && !search && (
              <button
                type="button"
                className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-xs text-muted-foreground hover:bg-accent"
                onClick={clear}
              >
                <X className="h-3 w-3" />
                Clear all
              </button>
            )}

            {needsAsyncSearch && !search && selectedIds.size === 0 && (
              <p className="px-2 py-3 text-center text-sm text-muted-foreground">
                Type to search schools
              </p>
            )}

            {needsAsyncSearch && search && searching && (
              <p className="px-2 py-3 text-center text-sm text-muted-foreground">
                Searching...
              </p>
            )}

            {needsAsyncSearch && search && !searching && searchResults.length === 0 && (
              <p className="px-2 py-3 text-center text-sm text-muted-foreground">
                No matches
              </p>
            )}

            {options.map((s) => (
              <label
                key={s.id}
                className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
              >
                <input
                  type="checkbox"
                  checked={selectedIds.has(s.id)}
                  onChange={() => toggle(s.id)}
                  className="h-3.5 w-3.5 rounded border-border"
                />
                {s.name}
              </label>
            ))}

            {!needsAsyncSearch && search && options.length === 0 && (
              <p className="px-2 py-3 text-center text-sm text-muted-foreground">
                No matches
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Returns the effective school IDs to use for data fetching.
 *
 * Priority:
 * 1. URL `schools=` param (explicit filter selection)
 * 2. Active school pins from PinContext
 * 3. Role-derived schools for school-scoped users (school_admin, coach)
 * 4. Empty array for super_admin / org_admin (no filter → show all)
 */
export function useSchoolFilterIds(): string[] {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const { activeSchoolIds } = usePinFilters();

  return useMemo(() => {
    // 1. Explicit URL filter takes priority
    const param = searchParams.get("schools");
    if (param) return param.split(",");

    // 2. Active pins act as filters
    if (activeSchoolIds.length > 0) return activeSchoolIds;

    // 3. School-scoped users auto-derive from roles
    const roles = user?.roles ?? [];
    const isSuperOrOrg = roles.some(
      (r) => r.role_type === "super_admin" || r.role_type === "org_admin",
    );
    if (isSuperOrOrg) return []; // no filter → show all

    const schoolIds = [...new Set(roles.filter((r) => r.scope_type === "school").map((r) => r.scope_id))];
    return schoolIds;
  }, [searchParams, user, activeSchoolIds]);
}
